import "./app.css";
import { Canvas, TopBar } from "./components";
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import {
  Navigate,
  Route,
  Routes,
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from "react-router-dom";
import React, { useEffect } from "react";
import { useUser } from "./contexts/user.context";
import { AppProviders } from "./contexts/AppProviders";
import API from "./utils/api";
import { getValidToken } from "./utils/getValidToken";
import AdminData from "../admin.data";
import { FavoriteProvider } from "./contexts/favorites.context";
import { ScheduleEventsProvider } from "./contexts/schedule.events.context";
import StudyPlanActuals from "../study.plan.actuals";
import StudyPlanSearch from "../study.plan.search";
import StudyPlans from "../study.plans";
import { StudyProvider } from "./contexts/study.context";
import StudySearchHome from "../study.plan.search/home";
import { nanoid } from "nanoid";
import UserProfile from "../user.profile";
import Goodbye from "../login/log.out";
import styled from "styled-components";
import { ReportOutlined } from "@material-ui/icons";
import { msalInstance, getLoginRequest, msalReady } from "./utils/msalInstance";
import SessionTimeoutManager from "./components/session.timeout.manager";
import { isSessionEnded } from "./utils/sessionEnded";

export { getLoginRequest as loginRequest };

// ─── Hook: silently refresh token when the tab regains focus ────────────────
function useTokenRefreshOnFocus() {
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && !isSessionEnded()) {
        try {
          await getValidToken();
        } catch (e) {
          // getValidToken handles redirects internally — nothing to do here
          console.debug("Token refresh on focus failed:", e);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);
}
// ─────────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <Canvas>
          <AppProviders msalInstance={msalInstance}>
            <TopBar />
            <div>
              <Routes>
                <Route
                  path="/"
                  element={
                    <SecureRoute>
                      <Navigate to="/ctpo/study-plans/" replace />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/ctpo/*"
                  element={
                    <SecureRoute>
                      <CTPO />
                    </SecureRoute>
                  }
                />
                <Route path="/goodbye" element={<Goodbye />} />
                <Route path="/health" element={<Health />} />
              </Routes>
            </div>
          </AppProviders>
        </Canvas>
      </Router>
    </MsalProvider>
  );
}

export default App;

const SecureRoute = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const [msalSettled, setMsalSettled] = React.useState(false);

  useEffect(() => {
    let alive = true;
    msalReady.finally(() => {
      if (alive) setMsalSettled(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!msalSettled || inProgress !== InteractionStatus.None) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <LoadingSpinner />;
  }

  return children;
};

const CTPO = () => {
  const { setUpdateNavigation, setURLStudyId } = useUser();
  const { instance, accounts, inProgress } = useMsal();
  const [isAppReady, setIsAppReady] = React.useState(false);
  const location = useLocation();

  // refresh token when user returns to the tab after idle
  useTokenRefreshOnFocus();

  useEffect(() => {
    setUpdateNavigation(nanoid());
    const studyId = location.pathname.split("/")[3];
    const pathContains =
      location.pathname.includes("study-plans") ||
      location.pathname.includes("study-actuals");
    setURLStudyId(pathContains && studyId ? studyId : null);
  }, [location.pathname, setUpdateNavigation, setURLStudyId]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length > 0) {
      if (!instance.getActiveAccount()) {
        instance.setActiveAccount(accounts[0]);
      }
      setIsAppReady(true);
    }
  }, [accounts, inProgress, instance]);

  if (!isAppReady || inProgress !== InteractionStatus.None) {
    return <LoadingSpinner />;
  }

  return (
    <FavoriteProvider>
      <StudyProvider>
        <ScheduleEventsProvider>
          <SessionTimeoutManager />
          <ContentArea />
        </ScheduleEventsProvider>
      </StudyProvider>
    </FavoriteProvider>
  );
};

// ─── ContentArea: guarded health check ──────────────────────────────────────
const ContentArea = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const checkHealth = async () => {
      try {
        // ensure we have a valid token BEFORE calling the API.
        // If the token is expired, this triggers silent refresh or redirect.
        // If a redirect happens, token is null and we bail out — no /health flash.
        const token = await getValidToken();
        if (!token || cancelled) return;

        await API.health();
      } catch (err) {
        if (cancelled) return;

        // 401/403 = auth issue, getValidToken will handle on next call
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          return;
        }

        // Aborted requests from the interceptor — redirect in progress
        if (err?.code === "ERR_CANCELED" || err?.__authRedirect) {
          return;
        }

        // Genuine network failure — only NOW show the health page
        console.error("Health check failed:", err);
        navigate("/health");
      }
    };

    checkHealth();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <Routes>
      <Route
        path="study-plans/:id/*"
        element={
          <SecureRoute>
            <StudyPlans />
          </SecureRoute>
        }
      />
      <Route
        path="study-plans/"
        element={
          <SecureRoute>
            <StudySearchHome />
          </SecureRoute>
        }
      />
      <Route
        path="study-actuals/:id/*"
        element={
          <SecureRoute>
            <StudyPlanActuals />
          </SecureRoute>
        }
      />
      <Route
        path="study-actuals/"
        element={
          <SecureRoute>
            <StudyPlanSearch />
          </SecureRoute>
        }
      />
      <Route
        path="admin-data/:id"
        element={
          <SecureRoute>
            <AdminData />
          </SecureRoute>
        }
      />
      <Route
        path="admin-data/"
        element={
          <SecureRoute>
            <AdminData />
          </SecureRoute>
        }
      />
      <Route
        path="user-profile/"
        element={
          <SecureRoute>
            <UserProfile />
          </SecureRoute>
        }
      />
    </Routes>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const Health = () => (
  <Container>
    <LogoutBox>
      <h2>Network Issues</h2>
      <ReportOutlined fontSize="large" />
    </LogoutBox>
  </Container>
);

const LoadingSpinner = () => (
  <Container>
    <LogoutBox>
      <h2>Loading...</h2>
    </LogoutBox>
  </Container>
);

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 91px);
`;

const LogoutBox = styled.div`
  width: 500px;
  height: 250px;
  background-color: var(--White);
  padding: 1em 2em;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 1em;
  justify-content: center;
  align-items: center;
`;
